import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

export default function AuditLogs() {
  const [logs, setLogs]   = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ action: '', resourceType: '', page: 1 });

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: 50, page: filters.page });
      if (filters.action)       params.set('action', filters.action);
      if (filters.resourceType) params.set('resourceType', filters.resourceType);
      const r = await api.get(`/tenant/audit-logs?${params}`);
      setLogs(r.data.logs || []);
      setTotal(r.data.total || 0);
    } catch (err) {
      if (err.response?.status === 403) {
        setLogs([]);
        toast.error('Audit logs require Enterprise plan');
      } else {
        toast.error(err.response?.data?.error || 'Failed');
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filters]);

  const actionColor = (a) => {
    if (a.includes('deleted') || a.includes('revoked') || a.includes('suspended')) return '#f38ba8';
    if (a.includes('created'))  return '#a6e3a1';
    if (a.includes('updated') || a.includes('changed')) return '#fab387';
    return 'var(--text-2)';
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <h2 style={{ fontSize: 16, fontWeight: 600 }}>Audit Logs ({total.toLocaleString()})</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <input value={filters.action} onChange={e => setFilters(f => ({ ...f, action: e.target.value, page: 1 }))} placeholder="Filter by action…" className="input" style={{ width: 160 }} />
          <input value={filters.resourceType} onChange={e => setFilters(f => ({ ...f, resourceType: e.target.value, page: 1 }))} placeholder="Resource type…" className="input" style={{ width: 140 }} />
          <button onClick={() => load()} className="btn btn-ghost" style={{ fontSize: 12 }}>Refresh</button>
        </div>
      </div>

      {loading ? <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-3)' }}>Loading…</div> : (
        logs.length === 0 ? (
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 40, textAlign: 'center', color: 'var(--text-3)' }}>
            No audit logs found. {total === 0 && 'Upgrade to Enterprise to enable audit logging.'}
          </div>
        ) : (
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--bg)' }}>
                  {['Action','Actor','Resource','IP','When'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '10px 14px', color: 'var(--text-3)', borderBottom: '1px solid var(--border)', fontWeight: 500 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {logs.map(l => (
                  <tr key={l.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '9px 14px' }}>
                      <code style={{ fontSize: 11, color: actionColor(l.action), fontWeight: 600 }}>{l.action}</code>
                    </td>
                    <td style={{ padding: '9px 14px' }}>
                      <div style={{ fontSize: 12 }}>{l.actor_name || '—'}</div>
                      <div style={{ fontSize: 10, color: 'var(--text-3)' }}>{l.actor_email}</div>
                    </td>
                    <td style={{ padding: '9px 14px', fontSize: 12, color: 'var(--text-2)' }}>
                      {l.resource_type ? `${l.resource_type}${l.resource_id ? ` #${l.resource_id.slice(0, 8)}` : ''}` : '—'}
                    </td>
                    <td style={{ padding: '9px 14px', fontSize: 11, color: 'var(--text-3)', fontFamily: 'monospace' }}>{l.ip_address || '—'}</td>
                    <td style={{ padding: '9px 14px', fontSize: 11, color: 'var(--text-3)', whiteSpace: 'nowrap' }}>{new Date(l.created_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderTop: '1px solid var(--border)' }}>
              <span style={{ fontSize: 12, color: 'var(--text-3)' }}>Page {filters.page} · {logs.length} entries</span>
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => setFilters(f => ({ ...f, page: Math.max(1, f.page - 1) }))} disabled={filters.page === 1} className="btn btn-ghost" style={{ fontSize: 12 }}>← Prev</button>
                <button onClick={() => setFilters(f => ({ ...f, page: f.page + 1 }))} disabled={logs.length < 50} className="btn btn-ghost" style={{ fontSize: 12 }}>Next →</button>
              </div>
            </div>
          </div>
        )
      )}
    </div>
  );
}
